/**
 * Boîte à outils — géométrie dans l'espace, logique pure (patrons, sections).
 *
 * Vecteurs, rotations (Rodrigues), vue orthographique orbitale (z vers le
 * haut), polyèdres convexes : arêtes vues / cachées, section par un plan.
 * Patrons : déplier un solide le long d'un arbre de faces, puis le replier
 * d'une fraction t (0 = à plat, 1 = fermé) — chaque face tourne autour de sa
 * charnière de t × (angle entre les normales extérieures).
 */

export type V3 = readonly [number, number, number];
export type P2 = readonly [number, number];

export const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const scale = (a: V3, k: number): V3 => [a[0] * k, a[1] * k, a[2] * k];
export const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: V3, b: V3): V3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
export const norm = (a: V3) => Math.hypot(a[0], a[1], a[2]);
export const normalize = (a: V3): V3 => {
  const n = norm(a);
  return n < 1e-12 ? [0, 0, 0] : scale(a, 1 / n);
};
export const lerp3 = (a: V3, b: V3, t: number): V3 => add(a, scale(sub(b, a), t));

export function centroid(pts: readonly V3[]): V3 {
  let s: V3 = [0, 0, 0];
  for (const p of pts) s = add(s, p);
  return scale(s, 1 / pts.length);
}

/** normale d'un polygone (méthode de Newell), sens trigonométrique vu de la pointe */
export function normal(pts: readonly V3[]): V3 {
  let n: V3 = [0, 0, 0];
  pts.forEach((p, i) => {
    const q = pts[(i + 1) % pts.length]!;
    n = add(n, [
      (p[1] - q[1]) * (p[2] + q[2]),
      (p[2] - q[2]) * (p[0] + q[0]),
      (p[0] - q[0]) * (p[1] + q[1]),
    ]);
  });
  return normalize(n);
}

/* ---------- déplacements ---------- */

/** p ↦ R p + t, R en lignes */
export interface Affine {
  r: readonly [number, number, number, number, number, number, number, number, number];
  t: V3;
}

export const IDENTITY: Affine = { r: [1, 0, 0, 0, 1, 0, 0, 0, 1], t: [0, 0, 0] };

export function apply(a: Affine, p: V3): V3 {
  const r = a.r;
  return [
    r[0] * p[0] + r[1] * p[1] + r[2] * p[2] + a.t[0],
    r[3] * p[0] + r[4] * p[1] + r[5] * p[2] + a.t[1],
    r[6] * p[0] + r[7] * p[1] + r[8] * p[2] + a.t[2],
  ];
}

/** a ∘ b : d'abord b, puis a */
export function compose(a: Affine, b: Affine): Affine {
  const x = a.r;
  const y = b.r;
  const r = [0, 0, 0, 0, 0, 0, 0, 0, 0] as [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      r[3 * i + j] = x[3 * i]! * y[j]! + x[3 * i + 1]! * y[3 + j]! + x[3 * i + 2]! * y[6 + j]!;
  return { r, t: apply(a, b.t) };
}

/** rotation d'angle θ autour de la droite (point, axe unitaire) — Rodrigues */
export function rotationAbout(point: V3, axis: V3, theta: number): Affine {
  const [x, y, z] = normalize(axis);
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const C = 1 - c;
  const r: Affine['r'] = [
    c + x * x * C,
    x * y * C - z * s,
    x * z * C + y * s,
    y * x * C + z * s,
    c + y * y * C,
    y * z * C - x * s,
    z * x * C - y * s,
    z * y * C + x * s,
    c + z * z * C,
  ];
  const rot: Affine = { r, t: [0, 0, 0] };
  // translation : p ↦ R(p − point) + point
  return { r, t: sub(point, apply(rot, point)) };
}

/* ---------- vue ---------- */

/** angles de vue en radians : `yaw` autour de la verticale, `pitch` au-dessus de l'horizon */
export interface View {
  yaw: number;
  pitch: number;
}

export interface Camera {
  /** vecteur unitaire de la scène vers l'observateur */
  toward: V3;
  right: V3;
  up: V3;
  /** coordonnées d'écran (x à droite, y en haut) et profondeur (grande = proche) */
  project: (p: V3) => { x: number; y: number; z: number };
}

export function camera(v: View): Camera {
  const cp = Math.cos(v.pitch);
  const toward: V3 = [cp * Math.cos(v.yaw), cp * Math.sin(v.yaw), Math.sin(v.pitch)];
  const right: V3 = [-Math.sin(v.yaw), Math.cos(v.yaw), 0];
  const up = cross(toward, right);
  return {
    toward,
    right,
    up,
    project: (p) => ({ x: dot(p, right), y: dot(p, up), z: dot(p, toward) }),
  };
}

export const PITCH_MIN = (-15 * Math.PI) / 180;
export const PITCH_MAX = (85 * Math.PI) / 180;
export const clampView = (v: View): View => ({
  yaw: ((v.yaw % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI),
  pitch: Math.min(PITCH_MAX, Math.max(PITCH_MIN, v.pitch)),
});

/* ---------- polyèdres ---------- */

export interface Face {
  /** sommets dans le sens trigonométrique vu de l'EXTÉRIEUR */
  pts: readonly V3[];
  /** surface lisse : les arêtes entre deux faces du même groupe ne se dessinent pas */
  smooth?: number;
}

const key = (p: V3) => p.map((c) => Math.round(c * 1e6) / 1e6).join(',');

export interface Edge {
  a: V3;
  b: V3;
  faces: number[];
}

export function edgesOf(faces: readonly Face[]): Edge[] {
  const map = new Map<string, Edge>();
  faces.forEach((f, i) => {
    f.pts.forEach((p, k) => {
      const q = f.pts[(k + 1) % f.pts.length]!;
      const kp = key(p);
      const kq = key(q);
      if (kp === kq) return;
      const id = kp < kq ? `${kp}|${kq}` : `${kq}|${kp}`;
      const e = map.get(id);
      if (e) e.faces.push(i);
      else map.set(id, { a: p, b: q, faces: [i] });
    });
  });
  return [...map.values()];
}

export interface EdgeLine {
  a: V3;
  b: V3;
  hidden: boolean;
}

/**
 * Arêtes à tracer d'un polyèdre convexe fermé : arêtes vives (entre deux
 * faces de groupes différents) et contours apparents des surfaces lisses ;
 * vue si l'une des faces voisines est tournée vers l'observateur, sinon en
 * pointillés.
 */
export function edgeLines(faces: readonly Face[], cam: Camera): EdgeLine[] {
  const front = faces.map((f) => dot(normal(f.pts), cam.toward) > 1e-9);
  const out: EdgeLine[] = [];
  for (const e of edgesOf(faces)) {
    const [i, j] = e.faces;
    if (j === undefined) {
      out.push({ a: e.a, b: e.b, hidden: !front[i!] });
      continue;
    }
    const fi = faces[i!]!;
    const fj = faces[j]!;
    const sameSmooth = fi.smooth !== undefined && fi.smooth === fj.smooth;
    const silhouette = front[i!] !== front[j];
    if (sameSmooth && !silhouette) continue;
    out.push({ a: e.a, b: e.b, hidden: !front[i!] && !front[j]! });
  }
  return out;
}

export const isFront = (f: Face, cam: Camera) => dot(normal(f.pts), cam.toward) > 1e-9;

/* ---------- section plane ---------- */

/** plan n · p = d (n unitaire) */
export interface Plane {
  n: V3;
  d: number;
}

export interface SectionPiece {
  a: V3;
  b: V3;
  face: number;
}

/** morceaux de la section dans chaque face (un segment par face coupée) */
export function sectionPieces(faces: readonly Face[], plane: Plane): SectionPiece[] {
  const out: SectionPiece[] = [];
  const side = (p: V3) => dot(plane.n, p) - plane.d;
  faces.forEach((f, idx) => {
    const hits: V3[] = [];
    f.pts.forEach((p, k) => {
      const q = f.pts[(k + 1) % f.pts.length]!;
      const sp = side(p);
      const sq = side(q);
      if (Math.abs(sp) < 1e-9) hits.push(p);
      else if (sp * sq < -1e-18 && Math.abs(sq) >= 1e-9) hits.push(lerp3(p, q, sp / (sp - sq)));
    });
    // dédoublonner
    const uniq: V3[] = [];
    for (const h of hits) if (!uniq.some((u) => norm(sub(u, h)) < 1e-7)) uniq.push(h);
    if (uniq.length === 2) out.push({ a: uniq[0]!, b: uniq[1]!, face: idx });
  });
  return out;
}

/** polygone de section ordonné (convexe), vide si le plan ne coupe pas */
export function sectionPolygon(pieces: readonly SectionPiece[], plane: Plane): V3[] {
  const pts: V3[] = [];
  for (const s of pieces)
    for (const p of [s.a, s.b]) if (!pts.some((u) => norm(sub(u, p)) < 1e-7)) pts.push(p);
  if (pts.length < 3) return [];
  const c = centroid(pts);
  const e1 = normalize(sub(pts[0]!, c));
  const e2 = cross(plane.n, e1);
  return pts
    .map((p) => ({ p, a: Math.atan2(dot(sub(p, c), e2), dot(sub(p, c), e1)) }))
    .sort((x, y) => x.a - y.a)
    .map((x) => x.p);
}

/* ---------- patrons ---------- */

export interface NetFace {
  /** polygone à plat (patron) */
  poly: readonly P2[];
  /** face mère (−1 pour la face posée) */
  parent: number;
  /** charnière commune avec la mère, dans le patron */
  hinge: readonly [P2, P2] | null;
  /** angle de pliage complet (radians) */
  angle: number;
}

const to3 = (p: P2): V3 => [p[0], p[1], 0];

/**
 * Faces repliées d'une fraction t ∈ [0 ; 1] : la face posée reste dans le
 * plan z = 0, les autres se relèvent vers z > 0 (l'extérieur du solide est
 * dessous à plat). Les mères précèdent leurs filles dans le tableau.
 */
export function foldNet(net: readonly NetFace[], t: number): V3[][] {
  const mats: Affine[] = [];
  return net.map((f, i) => {
    let m = IDENTITY;
    if (f.parent >= 0 && f.hinge) {
      const [A, B] = f.hinge;
      const c = f.poly.reduce<P2>(
        (s, p) => [s[0] + p[0] / f.poly.length, s[1] + p[1] / f.poly.length],
        [0, 0],
      );
      const cr = (B[0] - A[0]) * (c[1] - A[1]) - (B[1] - A[1]) * (c[0] - A[0]);
      const s = cr >= 0 ? 1 : -1;
      const local = rotationAbout(to3(A), sub(to3(B), to3(A)), s * t * f.angle);
      m = compose(mats[f.parent]!, local);
    }
    mats[i] = m;
    return f.poly.map((p) => apply(m, to3(p)));
  });
}

/** arbre de pliage : `parent[i]` = face mère de la face i (−1 : face posée, en premier) */
export interface SolidNet {
  faces: readonly Face[];
  order: readonly number[];
  parent: readonly number[];
}

/**
 * Déplie un polyèdre (faces orientées vers l'extérieur) le long de son arbre.
 * Renvoie les faces dans l'ordre `order` (la face posée d'abord).
 */
export function unfold(s: SolidNet): NetFace[] {
  const placed = new Map<number, { two: P2[]; idx: number }>();
  const out: NetFace[] = [];
  for (const fi of s.order) {
    const f = s.faces[fi]!;
    const n = normal(f.pts);
    const pi = s.parent[fi]!;
    if (pi < 0) {
      const o = f.pts[0]!;
      const e1 = normalize(sub(f.pts[1]!, o));
      const e2 = cross(e1, n); // extérieur vers −z une fois à plat
      const two = f.pts.map((p): P2 => [dot(sub(p, o), e1), dot(sub(p, o), e2)]);
      placed.set(fi, { two, idx: out.length });
      out.push({ poly: two, parent: -1, hinge: null, angle: 0 });
      continue;
    }
    const par = s.faces[pi]!;
    const pp = placed.get(pi);
    if (!pp) throw new Error('la mère doit être dépliée avant la fille');
    // charnière : deux sommets communs
    const common = f.pts
      .map((p) => par.pts.findIndex((q) => norm(sub(p, q)) < 1e-7))
      .map((j, k) => ({ j, k }))
      .filter((x) => x.j >= 0);
    if (common.length < 2) throw new Error('faces sans arête commune');
    const [ca, cb] = common;
    const a3 = f.pts[ca!.k]!;
    const b3 = f.pts[cb!.k]!;
    const A = pp.two[ca!.j]!;
    const B = pp.two[cb!.j]!;
    const len = Math.hypot(B[0] - A[0], B[1] - A[1]);
    const U: P2 = [(B[0] - A[0]) / len, (B[1] - A[1]) / len];
    // côté opposé à la mère
    const pc = pp.two.reduce<P2>((acc, p) => [acc[0] + p[0], acc[1] + p[1]], [0, 0]);
    const pcx = pc[0] / pp.two.length - A[0];
    const pcy = pc[1] / pp.two.length - A[1];
    const sideParent = U[0] * pcy - U[1] * pcx;
    const V: P2 = sideParent > 0 ? [U[1], -U[0]] : [-U[1], U[0]];
    const u3 = normalize(sub(b3, a3));
    const two = f.pts.map((p): P2 => {
      const r = sub(p, a3);
      const along = dot(r, u3);
      const h = norm(sub(r, scale(u3, along)));
      return [A[0] + along * U[0] + h * V[0], A[1] + along * U[1] + h * V[1]];
    });
    const angle = Math.acos(Math.max(-1, Math.min(1, dot(normal(par.pts), n))));
    placed.set(fi, { two, idx: out.length });
    out.push({ poly: two, parent: pp.idx, hinge: [A, B], angle });
  }
  return out;
}
